import { createAsyncThunk } from "@reduxjs/toolkit";
import { notificationApi } from "../../api/notificationApi";
import { AppDispatch, RootState } from "../store";
import { NotificationFetchParameters, NotificationWithPagination } from "../../types/notificationType";
import { handleRequest } from "../utils/handleRequest";
import i18n from "../../i18n";

export const fetchUnreadNotificationAmountThunk = createAsyncThunk<
  number,
  void,
  { dispatch: AppDispatch; state: RootState }
>(
  "notification/unreadNotificationAmount",
  async (_, { dispatch }) => {
    const res = await handleRequest<number>(
      dispatch,
      "indirect",
      () => notificationApi.getUnreadNotificationAmount()
    );
    if (res.success() && typeof res.result === "number") {
      return res.result;
    }
    throw new Error(res.errorMessage ?? "Failed to fetch unread notifications");
  }
);

export const fetchNotificationListThunk = createAsyncThunk<
  NotificationWithPagination,
  NotificationFetchParameters,
  { dispatch: AppDispatch; state: RootState }
>(
  "notification/fetchNotificationList",
  async (fetchParameters, { dispatch }) => {
    const res = await handleRequest<NotificationWithPagination>(
      dispatch,
      "indirect",
      () => notificationApi.getNotificationList(
        fetchParameters.offset,
        fetchParameters.limit,
        fetchParameters.status
      )
    );
    if (res.success() && res.result) {
      return res.result;
    }
    throw new Error(res.errorMessage ?? "Failed to fetch notifications");
  }
);

export const editNotificationVisibilityThunk = createAsyncThunk<
  { id: number; status: string },
  void,
  { dispatch: AppDispatch; state: RootState }
>(
  "notification/editNotificationVisibility",
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const notificationToEdit = state.notifications.notificationToEdit;

    const notificationId = notificationToEdit.id ?? 0;
    const notificationStatus = notificationToEdit.status ?? "";

    const res = await handleRequest<void>(
      dispatch,
      "direct",
      () => notificationApi.editNotificationVisibility(notificationId, notificationStatus),
      i18n.t("requestMessage.notificationVisibilityUpdated")
    );
    if (res.success()) {
      await dispatch(fetchUnreadNotificationAmountThunk());
      return { id: notificationId, status: notificationStatus };
    }

    throw new Error(res.errorMessage ?? "Failed to update notification visibility");
  }
);